/**
 * BookingActions — submit / confirm / cancel for a single booking.
 *
 * ALL NON-OPTIMISTIC. Submit is low-stakes (one click, await, toast). Confirm
 * and cancel are high-stakes (billing + PMS): they require an explicit
 * confirmation Dialog, show a loading state while in flight, await the backend,
 * and surface a 409 invalid-state distinctly from a generic failure.
 */

import { useCancelBooking, useConfirmBooking, useSubmitBooking } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@hostesa/ui';
import { useState } from 'react';
import type { Booking } from './_deps';
import { isConflict } from './_deps';
import { type BookingAction, validBookingActions } from './booking-status';
import { namespace } from './i18n';

export interface BookingActionsProps {
  tenantId: string;
  hotelId: string;
  booking: Booking;
}

export function BookingActions({ tenantId, hotelId, booking }: BookingActionsProps) {
  const { t } = useT(namespace);

  const submitBooking = useSubmitBooking(tenantId, hotelId);
  const confirmBooking = useConfirmBooking(tenantId, hotelId);
  const cancelBooking = useCancelBooking(tenantId, hotelId);

  // The high-stakes action awaiting confirmation in the Dialog, if any.
  const [pendingAction, setPendingAction] = useState<'confirm' | 'cancel' | null>(null);

  const actions = validBookingActions(booking.status);
  const busy = submitBooking.isPending || confirmBooking.isPending || cancelBooking.isPending;

  async function run(action: BookingAction): Promise<void> {
    const mutation =
      action === 'submit' ? submitBooking : action === 'confirm' ? confirmBooking : cancelBooking;
    try {
      await mutation.mutateAsync({ bookingId: booking.id });
      toast.success(
        t(
          action === 'submit'
            ? 'actions.submitted'
            : action === 'confirm'
              ? 'actions.confirmed'
              : 'actions.cancelled',
        ),
      );
    } catch (err) {
      toast.error(isConflict(err) ? t('actions.conflict') : t('actions.error'));
    } finally {
      setPendingAction(null);
    }
  }

  if (actions.length === 0) {
    return (
      <p className="text-sm text-[var(--color-stone-mid)]" data-testid="booking-no-actions">
        {t('actions.none')}
      </p>
    );
  }

  const dialogTitleKey =
    pendingAction === 'confirm' ? 'confirmDialog.confirmTitle' : 'confirmDialog.cancelTitle';
  const dialogBodyKey =
    pendingAction === 'confirm' ? 'confirmDialog.confirmBody' : 'confirmDialog.cancelBody';

  return (
    <div className="flex flex-wrap gap-2" data-testid="booking-actions">
      {actions.includes('submit') && (
        <Button
          data-testid="booking-submit-btn"
          size="sm"
          disabled={busy}
          onClick={() => void run('submit')}
        >
          {submitBooking.isPending ? t('actions.submitting') : t('actions.submit')}
        </Button>
      )}
      {actions.includes('confirm') && (
        <Button
          data-testid="booking-confirm-btn"
          size="sm"
          disabled={busy}
          onClick={() => setPendingAction('confirm')}
        >
          {confirmBooking.isPending ? t('actions.confirming') : t('actions.confirm')}
        </Button>
      )}
      {actions.includes('cancel') && (
        <Button
          data-testid="booking-cancel-btn"
          size="sm"
          variant="destructive"
          disabled={busy}
          onClick={() => setPendingAction('cancel')}
        >
          {cancelBooking.isPending ? t('actions.cancelling') : t('actions.cancel')}
        </Button>
      )}

      <Dialog
        open={pendingAction !== null}
        onOpenChange={(o) => {
          if (!o) setPendingAction(null);
        }}
      >
        <DialogContent data-testid="booking-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{pendingAction ? t(dialogTitleKey) : ''}</DialogTitle>
            <DialogDescription>{pendingAction ? t(dialogBodyKey) : ''}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              data-testid="booking-dialog-dismiss-btn"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => setPendingAction(null)}
            >
              {t('confirmDialog.dismiss')}
            </Button>
            <Button
              data-testid="booking-dialog-proceed-btn"
              size="sm"
              variant={pendingAction === 'cancel' ? 'destructive' : 'primary'}
              disabled={busy}
              onClick={() => pendingAction && void run(pendingAction)}
            >
              {busy ? t('actions.confirming') : t('confirmDialog.proceed')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
